import { Box, VStack, HStack, SimpleGrid, Text, Button, Heading, Image, Tag } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaCut, FaShoppingBag, FaStar, FaUser, FaRandom, FaCrown } from 'react-icons/fa';
import { FaCashRegister } from 'react-icons/fa6';

const MotionBox = motion(Box);

const MainMenu = ({ userName, isMensalista, isAdmin, onLogout, onFinalizarAtendimento, onBecomeMember }) => {
  const navigate = useNavigate();

  // Menu para clientes normais
  const clientMenuItems = [
    {
      id: 'profile',
      name: 'Meu Perfil',
      icon: FaUser,
      path: '/profile',
      color: 'teal.300'
    },
    {
      id: 'gallery',
      name: 'Galeria de Estilos',
      icon: FaCut,
      path: '/gallery',
      color: 'purple.400'
    },
    {
      id: 'produtos',
      name: 'Produtos',
      icon: FaShoppingBag,
      path: '/products',
      color: 'orange.400'
    },
    {
      id: 'avaliacao',
      name: 'Avaliação',
      icon: FaStar,
      path: '/feedback',
      color: 'yellow.400'
    },
    // Opção para se tornar sócio (mensalista) - mostrado apenas para clientes avulsos
    !isMensalista ? {
      id: 'tornar-socio',
      name: 'Quero me tornar sócio',
      icon: FaCrown,
      path: '/become-member',
      color: 'yellow.500',
      special: true
    } : null
  ].filter(Boolean); // Remove itens nulos do array
  
  // Menu para administradores - apenas com opção de Sorteio
  const adminMenuItems = [
    {
      id: 'sorteio',
      name: 'Realizar Sorteio',
      icon: FaRandom,
      path: '/admin/sorteio',
      color: 'purple.400'
    }
  ];
  
  // Seleciona o menu apropriado com base no tipo de usuário
  const menuItems = isAdmin ? adminMenuItems : clientMenuItems;

  // Checkout destacado, sempre o último
  const checkoutItem = {
    id: 'checkout',
    name: 'Checkout',
    icon: FaCashRegister,
    path: '/checkout',
    color: 'yellow.400',
    highlight: true
  };

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      height="100vh"
      width="100%"
      bg="black"
      padding={6}
    >
      <VStack spacing={6} height="100%">
        <Box w="100%" display="flex" alignItems="center" justifyContent="center" mt={4} mb={2}>
          <Image src={require('../assets/Logo-Barber.png')} alt="Logo Barbearia" style={{ height: 110, objectFit: 'contain', filter: 'drop-shadow(0 6px 32px #000a)' }} />
        </Box>
        <Heading color="yellow.400" fontSize={{ base: '3xl', md: '4xl' }} mb={2} textAlign="center" fontWeight="extrabold" letterSpacing="1px" textShadow="0 2px 12px #0008">
          Menu Principal
        </Heading>
        <HStack width="100%" justifyContent="space-between" alignItems="center">
          <Box>
            <HStack mt={1}>
              <Text color="white" fontSize="xl">Olá, <span style={{fontWeight:700, color:'#fff'}}>{userName}</span></Text>
              {isMensalista && (
                 <Tag px={4} py={1} fontWeight="bold" fontSize="md" borderRadius="md" color="black" bgGradient="linear(to-b, yellow.400, yellow.500)" boxShadow="0 2px 12px #ECC94B88, 0 0px 0px #0000">MENSALISTA</Tag>
              )}
              {isAdmin && (
                 <Tag px={4} py={1} fontWeight="bold" fontSize="md" borderRadius="md" color="white" bgGradient="linear(to-b, red.500, red.600)" boxShadow="0 2px 12px rgba(229, 62, 62, 0.5), 0 0px 0px #0000">ADMINISTRADOR</Tag>
              )}
            </HStack>
          </Box>
          <Button variant="outline" colorScheme="whiteAlpha" size="sm" onClick={onLogout}>Sair</Button>
        </HStack>

        {/* Grid moderno preenchendo mais a tela, espaçamento vertical generoso */}
        <SimpleGrid 
          columns={{ base: 1, sm: 1, md: isAdmin ? 1 : 2, lg: isAdmin ? 1 : 2 }} 
          spacingY={14} 
          spacingX={10} 
          mt={12} 
          mb={10} 
          minH="60vh"
          justifyItems="center"
          maxW="1200px"
          mx="auto"
        >
          {menuItems.map((item) => (
            <Box
              key={item.id}
              as={Button}
              onClick={() => item.special ? onBecomeMember() : navigate(item.path)}
              bg={isAdmin ? "yellow.400" : item.special ? "yellow.500" : "gray.900"}
              color={isAdmin || item.special ? "black" : "white"}
              fontSize={isAdmin ? "3xl" : "2xl"}
              fontWeight="extrabold"
              borderRadius="2xl"
              boxShadow={isAdmin || item.special ? "0 6px 32px #ECC94B99, 0 0px 0px #0000" : "0 4px 32px #000a"}
              py={isAdmin ? 24 : 18}
              px={isAdmin ? 16 : 12}
              minH={isAdmin ? "240px" : "200px"}
              minW={isAdmin ? "320px" : "300px"}
              maxW={isAdmin ? "400px" : "350px"}
              w="100%"
              h="auto"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              _hover={{ bg: isAdmin ? 'yellow.300' : item.special ? 'yellow.400' : 'gray.800', transform: 'scale(1.04)' }}
              _active={{ bg: isAdmin ? 'yellow.600' : item.special ? 'yellow.600' : 'gray.700', transform: 'scale(0.98)' }}
              transition="all 0.18s cubic-bezier(.4,1,.3,1)"
              mb={2}
              letterSpacing="0.5px"
            >
              <Box mb={4}>{item.icon && <item.icon size={64} color={item.color} />}</Box>
              <Text fontSize={isAdmin ? "3xl" : "2xl"} textAlign="center">{item.name}</Text>
            </Box>
          ))}
          {/* Botão Checkout destacado - mostrado apenas para clientes normais */}
          {!isAdmin && (
            <Box
              as={Button}
              onClick={() => navigate(checkoutItem.path)}
              bgGradient="linear(to-b, yellow.400, yellow.500)"
              color="black"
              fontSize="2xl"
              fontWeight="extrabold"
              borderRadius="2xl"
              boxShadow="0 6px 32px #ECC94B99, 0 0px 0px #0000"
              py={18}
              px={12}
              minH="200px"
              minW="300px"
              maxW="350px"
              w="100%"
              h="auto"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              transition="all 0.18s cubic-bezier(.4,1,.3,1)"
              _hover={{ bg: 'yellow.300', transform: 'scale(1.06)' }}
              _active={{ bg: 'yellow.600', transform: 'scale(0.97)' }}
              mb={2}
              letterSpacing="0.5px"
            >
              <Box mb={4}>{checkoutItem.icon && <checkoutItem.icon size={64} color={checkoutItem.color} />}</Box>
              <Text fontSize="2xl" textAlign="center">{checkoutItem.name}</Text>
            </Box>
          )}
        </SimpleGrid>

        <Box width="100%" textAlign="center" py={4}>
          <Text fontSize="sm" color="whiteAlpha.600">
            2025 Barbearia Freire • Todos os direitos reservados
          </Text>
        </Box>
      </VStack>
    </MotionBox>
  );
};

export default MainMenu;
